import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Vega } from "react-vega";
import { Handler } from "vega-tooltip";
import { useTranslation } from "react-i18next";
import seedrandom from "seedrandom";

import className from "./style.module.scss";

export function VegaPlot(props) {
  const {
    t,
    // i18n
  } = useTranslation("translation");

  const DEFAULT_PLOT_MAX_WIDTH = 500;

  const [plotSpec, setPlotSpec] = useState(props.plotData.plotSpec);
  let plotData = [];
  const [plotWidth, setPlotWidth] = useState(plotSpec.width);
  const [plotHeight, setPlotHeight] = useState(plotSpec.height);
  const [altTextParams, setAltTextParams] = useState({});

  useEffect(() => {
    // Dynamic vega plot size support issues mean we need custom logic
    setPlotSize();
    window.addEventListener("resize", setPlotSize);
    return () => {
      window.removeEventListener("resize", setPlotSize);
    };
  }, []);

  useEffect(() => {
    updatePlotData();
  }, [props.controlData]);

  // Evaluate a plot's inline source (plotFunctionTxt / altTextFunctionTxt) into
  // a callable. Used when no bundled calculation function is provided.
  const evalTxtFunc = (txt) => {
    const funcString = txt
      .replace(new RegExp("const updateFunc =", "g"), "")
      .replace(new RegExp("export default updateFunc;", "g"), "")
      .replace(new RegExp("<br/>", "g"), " ");
    return eval(funcString);
  };

  // A bundled calculation function (when provided) takes precedence; otherwise
  // fall back to evaluating the inline source carried in the scene data. This
  // lets the production app render from S3 scene data with no bundled JS, while
  // bundled callers keep working unchanged.
  const runCalc = (bundledFn, txt, args) => {
    if (typeof bundledFn === "function") {
      return bundledFn(args);
    }
    if (txt) {
      // updatePlotData runs inside a useEffect, and React error boundaries do
      // not catch throws from effect callbacks — so a malformed inline source
      // (bad syntax, or a function that throws) would white-screen the whole
      // widget. Guard it so one bad plot degrades to empty data instead.
      try {
        return evalTxtFunc(txt)(args);
      } catch (e) {
        console.error("Failed to evaluate inline plot source:", e);
        return undefined;
      }
    }
    return undefined;
  };

  const updatePlotData = () => {
    const seedVar = Math.random();
    const args = { ...props.controlData, seedVar, seedrandom };

    const result = runCalc(
      props.plotCalculation,
      props.plotData.plotFunctionTxt,
      args
    );
    plotData = result === undefined ? [] : result;
    updatePlotSpec();

    const altTextResult = runCalc(
      props.altTextCalculation,
      props.plotData.altTextFunctionTxt,
      args
    );
    setAltTextParams(altTextResult || {});
  };

  const updatePlotSpec = () => {
    const updatedPlotSpec = plotSpec;
    updatedPlotSpec.data[0].values = plotData;
    setPlotSpec({ ...updatedPlotSpec });
  };

  const setPlotSize = () => {
    const width = plotSpec.width;
    const height = plotSpec.height;
    const aspectRatio = width / height;
    const elem = document.getElementById(`plot_${props.plotData.title}`);

    let newWidth = parseFloat(elem.offsetWidth) - 40;
    let newHeight = (parseFloat(elem.offsetWidth) - 40) / aspectRatio;

    if (newWidth > (plotSpec.maxWidth || DEFAULT_PLOT_MAX_WIDTH)) {
      newWidth = (plotSpec.maxWidth || DEFAULT_PLOT_MAX_WIDTH) - 40;
      newHeight =
        ((plotSpec.maxWidth || DEFAULT_PLOT_MAX_WIDTH) - 40) / aspectRatio;
    }

    setPlotWidth(newWidth);
    setPlotHeight(newHeight);
  };

  const renderDevFeatures = () => {
    return (
      <div className={className.Debug}>
        <div>
          Alt-text: <br />
          {t(`${props.plotData.altText}.string`, altTextParams)}
        </div>
        {props.plotData.recordURL !== undefined ? (
          <button
            onClick={() => {
              window.open(props.plotData.recordURL, "_blank");
            }}
          >
            Airtable record
          </button>
        ) : null}
      </div>
    );
  };

  return (
    <div
      id={`plot_${props.plotData.title}`}
      className={className.PlotContainer}
      style={{
        aspectRatio: plotSpec.width / plotSpec.height,
        width: "calc(100% - 20px)",
        maxWidth: `calc((50vh*${plotSpec.width / plotSpec.height}) - 40)`,
      }}
    >
      <h4 className="viz-title">{t(`${props.plotData.title}.string`)}</h4>
      <div
        aria-label={
          props.plotData.altText !== ""
            ? t(`${props.plotData.altText}.string`, altTextParams)
            : ""
        }
      >
        <Vega
          spec={plotSpec}
          data={plotData}
          tooltip={new Handler().call}
          width={plotWidth}
          height={plotHeight}
        />

        {props.dev === true ? renderDevFeatures() : null}
      </div>
    </div>
  );
}

VegaPlot.propTypes = {
  plotData: PropTypes.shape({
    title: PropTypes.string.isRequired,
    plotFunction: PropTypes.string.isRequired,
    plotSpec: PropTypes.object.isRequired,
  }).isRequired,
  controlData: PropTypes.object,
  // plotCalculation: PropTypes.func.isRequired,
  id: PropTypes.number.isRequired,
  widgetID: PropTypes.number.isRequired,
  dev: PropTypes.bool,
};
VegaPlot.defaultProps = {
  controlData: {},
  dev: false,
};
